import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { User, onAuthStateChanged, updateProfile } from 'firebase/auth';
import { auth } from '../firebase/config/firebase';
import {
  StudentProfile,
  AuthState,
  AppError,
  StudentProfileCompletionInput,
  AdminRole,
  StaffAccount,
} from '../types';
import {
  AuthContextValue,
  LoginCredentials,
  RegisterCredentials,
} from './authTypes';
import { authService } from './authService';
import { mapFirebaseAuthError, createError } from '../utils/errors';
import { isSuperAdminUser } from './RouteGuards';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [studentProfile, setStudentProfile] = useState<StudentProfile | null>(null);
  const [staffProfile, setStaffProfile] = useState<StaffAccount | null>(null);
  const [adminRole, setAdminRole] = useState<AdminRole | null>(null);
  const [authState, setAuthState] = useState<AuthState>('UNKNOWN');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<AppError | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isSuperAdmin = adminRole === 'SUPER_ADMIN' || (user ? isSuperAdminUser(user) : false);
  const isOperator = adminRole === 'OPERATOR';

  const hasSectionAccess = useCallback(
    (sectionKey: string): boolean => {
      if (isSuperAdmin) return true;
      if (isOperator) {
        const defaultAllowed = [
          'DASHBOARD',
          'BOOKINGS',
          'TRIPS',
          'STUDENTS',
          'CREATE_STUDENT',
          'DEMAND',
          'VEHICLES',
          'UNIVERSITIES',
          'NOTIFICATIONS',
        ];
        const allowed =
          staffProfile?.allowedSections && staffProfile.allowedSections.length > 0
            ? staffProfile.allowedSections
            : defaultAllowed;
        return allowed.includes(sectionKey);
      }
      return false;
    },
    [isSuperAdmin, isOperator, staffProfile]
  );

  // Fetch or refresh the Firestore profile (student or staff)
  const syncProfile = useCallback(async (firebaseUser: User) => {
    const isSuper = isSuperAdminUser(firebaseUser);
    if (isSuper) {
      setAdminRole('SUPER_ADMIN');
    }

    try {
      // 1. Check if user is an operational staff member in admins collection
      const staffDoc = await authService.fetchStaffProfile(firebaseUser.uid, firebaseUser.email);
      if (staffDoc) {
        setStaffProfile(staffDoc);
        setAdminRole(staffDoc.role || 'OPERATOR');
        if (staffDoc.status === 'SUSPENDED') {
          setAuthState('SUSPENDED');
          return;
        }
        setAuthState('AUTHENTICATED');
        if (typeof window !== 'undefined' && staffDoc.fullName) {
          localStorage.setItem('oto_current_student_name', staffDoc.fullName);
        }
        return;
      }

      // 1.1 If not in admins doc but email ends with @otostudents.com and not super admin, bootstrap as OPERATOR
      if (!isSuper && firebaseUser.email?.toLowerCase().endsWith('@otostudents.com')) {
        const username = firebaseUser.email.split('@')[0];
        const defaultStaff: StaffAccount = {
          uid: firebaseUser.uid,
          fullName: firebaseUser.displayName || username,
          username,
          email: firebaseUser.email,
          role: 'OPERATOR',
          status: 'ACTIVE',
          allowedSections: [
            'DASHBOARD',
            'BOOKINGS',
            'TRIPS',
            'STUDENTS',
            'CREATE_STUDENT',
            'DEMAND',
            'VEHICLES',
            'UNIVERSITIES',
          ],
          createdAt: new Date().toISOString(),
        };
        setStaffProfile(defaultStaff);
        setAdminRole('OPERATOR');
        setAuthState('AUTHENTICATED');
        if (typeof window !== 'undefined' && defaultStaff.fullName) {
          localStorage.setItem('oto_current_student_name', defaultStaff.fullName);
        }
        return;
      }

      // 2. Otherwise handle student profile
      let profile = await authService.fetchProfile(firebaseUser.uid);
      if (!profile && isSuper) {
        const emailLower = (firebaseUser.email || '').toLowerCase();
        profile = {
          uid: firebaseUser.uid,
          fullName: emailLower.includes('whdan')
            ? 'وهدان (مشرف النظام)'
            : emailLower.includes('nashgo')
            ? 'إدارة نشجو (مشرف النظام)'
            : 'عمر خالد (مدير المنظومة)',
          email: firebaseUser.email || '',
          address: 'الإدارة المركزية - الإسكندرية',
          universityGovernorate: 'الإسكندرية',
          universityId: 'admin_hq',
          universityName: 'إدارة OTO STUDENTS',
          status: 'ACTIVE',
          profileCompleted: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString(),
        };
      }

      if (profile) {
        if (typeof window !== 'undefined' && profile.fullName) {
          localStorage.setItem('oto_current_student_name', profile.fullName);
        }
        if (profile.fullName && (!firebaseUser.displayName || firebaseUser.displayName.includes('طالب OTO'))) {
          updateProfile(firebaseUser, { displayName: profile.fullName }).catch(() => {});
        }
        if (isSuper) {
          profile.profileCompleted = true;
          profile.status = 'ACTIVE';
        }
        setStudentProfile(profile);
        if (profile.status === 'SUSPENDED') {
          setAuthState('SUSPENDED');
        } else if (!profile.profileCompleted) {
          setAuthState('PROFILE_INCOMPLETE');
        } else {
          setAuthState('AUTHENTICATED');
        }
      } else {
        // In case student document is pending creation
        setAuthState(isSuper ? 'AUTHENTICATED' : 'PROFILE_INCOMPLETE');
      }
    } catch (err) {
      console.error('Error fetching student profile:', err);
      setAuthState(isSuper ? 'AUTHENTICATED' : 'PROFILE_INCOMPLETE');
    }
  }, []);

  // Central listener for Firebase Authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setIsLoading(true);
      if (firebaseUser) {
        setUser(firebaseUser);
        await syncProfile(firebaseUser);
      } else {
        setUser(null);
        setStudentProfile(null);
        setStaffProfile(null);
        setAdminRole(null);
        setAuthState('UNAUTHENTICATED');
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [syncProfile]);

  // Login handler
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<boolean> => {
      setIsLoading(true);
      setError(null);
      try {
        const userCred = await authService.login(credentials);
        setUser(userCred.user);
        await syncProfile(userCred.user);
        setIsLoading(false);
        return true;
      } catch (err: unknown) {
        const appError = mapFirebaseAuthError(err as { code?: string; message?: string });
        setError(appError);
        setIsLoading(false);
        return false;
      }
    },
    [syncProfile]
  );

  // Register handler
  const register = useCallback(
    async (credentials: RegisterCredentials): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      // Client-side password confirmation check
      if (credentials.password !== credentials.confirmPassword) {
        setError(createError('AUTH_PASSWORDS_MISMATCH'));
        setIsLoading(false);
        return false;
      }

      try {
        const { userCredential, profile } = await authService.register(credentials);
        setUser(userCredential.user);
        setStudentProfile(profile);
        setAuthState('PROFILE_INCOMPLETE');
        setIsLoading(false);
        return true;
      } catch (err: unknown) {
        const appError = mapFirebaseAuthError(err as { code?: string; message?: string });
        setError(appError);
        setIsLoading(false);
        return false;
      }
    },
    []
  );

  // Logout handler
  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await authService.logout();
      setUser(null);
      setStudentProfile(null);
      setStaffProfile(null);
      setAdminRole(null);
      setAuthState('UNAUTHENTICATED');
    } catch (err: unknown) {
      setError(createError('NETWORK_ERROR', String(err)));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Send Password Reset Email
  const resetPassword = useCallback(async (email: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await authService.sendPasswordReset(email);
      setIsLoading(false);
      return true;
    } catch (err: unknown) {
      const appError = mapFirebaseAuthError(err as { code?: string; message?: string });
      setError(appError);
      setIsLoading(false);
      return false;
    }
  }, []);

  // Complete Student Profile
  const completeProfile = useCallback(
    async (data: StudentProfileCompletionInput): Promise<boolean> => {
      if (!user) {
        setError(createError('UNAUTHENTICATED_STUDENT'));
        return false;
      }

      setIsLoading(true);
      setError(null);
      try {
        const updatedProfile = await authService.completeProfile(user.uid, data);
        setStudentProfile(updatedProfile);
        setAuthState('AUTHENTICATED');
        setIsLoading(false);
        return true;
      } catch (err: unknown) {
        setError(createError('NETWORK_ERROR', String(err)));
        setIsLoading(false);
        return false;
      }
    },
    [user]
  );

  // Manually refresh profile from Firestore
  const refreshProfile = useCallback(async () => {
    if (user) {
      await syncProfile(user);
    }
  }, [user, syncProfile]);

  const value: AuthContextValue = {
    user,
    studentProfile,
    staffProfile,
    adminRole,
    isOperator,
    isSuperAdmin,
    hasSectionAccess,
    authState,
    isLoading,
    error,
    login,
    register,
    logout,
    resetPassword,
    completeProfile,
    refreshProfile,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
